/**
 * SPA location awareness.
 *
 * Requirement: "we must always know where we are navigated to and what is displaying."
 *  · `hash`     — where the user navigated (the address bar is the source of truth)
 *  · `active`   — which section is actually on screen right now (scroll-spy)
 *  · `mounted`  — every <Section> currently rendered, in DOM order
 */
import { useEffect, useState, useSyncExternalStore } from 'react';

/* ---- hash --------------------------------------------------------------- */

const hashListeners = new Set<() => void>();
const getHash = () => window.location.hash || '#home';

window.addEventListener('hashchange', () => hashListeners.forEach((l) => l()));

export const useHash = (): string =>
  useSyncExternalStore(
    (cb) => { hashListeners.add(cb); return () => hashListeners.delete(cb); },
    getHash,
    () => '#home',
  );

/* ---- what is on screen -------------------------------------------------- */

export function useActiveSection(): string {
  const [active, setActive] = useState('home');

  useEffect(() => {
    const targets = Array.from(document.querySelectorAll<HTMLElement>('[data-anchor]'));
    if (targets.length === 0) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.getAttribute('data-anchor') ?? 'home');
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: [0, 0.25, 0.5, 1] },
    );

    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);

  return active;
}

/* ---- which sections are mounted ----------------------------------------- */

const mounted = new Set<string>();
const mountListeners = new Set<() => void>();
let snapshot: string[] = [];

function publish() {
  snapshot = [...mounted].sort();
  mountListeners.forEach((l) => l());
}

export function registerMounted(name: string): () => void {
  mounted.add(name);
  publish();
  return () => { mounted.delete(name); publish(); };
}

export const useMountedSections = (): string[] =>
  useSyncExternalStore(
    (cb) => { mountListeners.add(cb); return () => mountListeners.delete(cb); },
    () => snapshot,
    () => snapshot,
  );
